// openHouseDataService.js
// Service for real-time open house data management
import { useState, useEffect } from 'react';
import agent from '../agent'; // Import agent configuration
import simpleCache from './simpleCache'; // Import simple caching
import { getGalleryImageUrl } from '../utils/imageUtils';

// Module-level variables for singleton pattern
let openHousesData = null;
let isDataLoading = false;
let dataLoadPromise = null;

// Cache version - increment when filtering logic changes to invalidate old caches
const CACHE_VERSION = 2; // Incremented when date filtering logic changed

/**
 * Apply date filtering logic based on current day
 * 
 * Rules:
 * - Show today's open houses (even if they've already passed)
 * - Show future open houses through the end of the current week (Sunday)
 * - Exclude past days - when the day transitions, previous day's events drop off
 *   (e.g., on Saturday, Friday's events are no longer shown)
 */
const getRelevantOpenHouses = (openHouseArray) => {
  const now = new Date();
  const currentDay = now.getDay(); // 0=Sunday, 6=Saturday
  
  // Calculate the start of today (midnight) in local time
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  
  // Calculate the end of the current week (Sunday at 11:59:59 PM) in local time
  const endOfCurrentWeek = new Date(now);
  const daysUntilSunday = currentDay === 0 ? 0 : 7 - currentDay;
  endOfCurrentWeek.setDate(now.getDate() + daysUntilSunday);
  endOfCurrentWeek.setHours(23, 59, 59, 999);
  
  // Show events from start of today through end of current week
  // Filter by comparing the DATE portion, not just timestamp, to handle timezone correctly
  // Each open house event is filtered individually - if a property has both Friday and Saturday
  // events, only the Saturday event should show on Saturday
  // Note: We show today's events even if they've already passed, but exclude past days
  return openHouseArray.filter(oh => {
    // Ensure we have a valid Date object
    let startTime;
    if (oh.openHouseStartTime instanceof Date) {
      startTime = oh.openHouseStartTime;
    } else if (typeof oh.openHouseStartTime === 'string' || typeof oh.openHouseStartTime === 'number') {
      startTime = new Date(oh.openHouseStartTime);
    } else {
      // Invalid date, exclude this event
      return false;
    }
    
    // Compare the full timestamp - Date objects handle timezone conversion automatically
    // A Friday 8PM UTC event will be correctly identified as earlier than Saturday midnight local
    // This ensures: Wednesday/Thursday events are excluded, Friday events are excluded on Saturday
    // Only today's events (even if past) and future events through Sunday are shown
    return startTime >= startOfToday && startTime <= endOfCurrentWeek;
  });
};

/**
 * Fetch detailed listing information for a single listing using ListingKey
 */
const fetchListingDetails = async (listingKey) => {
  try {
    const baseUrl = import.meta.env.MODE === 'production' || import.meta.env.MODE === 'staging' 
      ? 'https://rmkp-open-houses.onrender.com' 
      : 'http://localhost:4000';
    
    const url = `${baseUrl}/api/listing-details/${listingKey}`;
    
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'X-Agent-ID': agent.sparkAgentId,
      },
      credentials: import.meta.env.MODE === 'production' ? 'include' : 'same-origin'
    });

    if (!res.ok) {
      return null;
    }

    const data = await res.json();
    return data;
  } catch (error) {
    return null;
  }
};

/**
 * Transform API response to open house entries
 * Each open house event becomes a separate entry
 */
const transformOpenHouseData = (rawData) => {
  
  if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
    return [];
  }

  const openHouses = [];

  rawData.forEach((listing, index) => {
    
    // Check if this listing has open house data (flat structure)
    if (!listing.OpenHouseStartTime) {
      return;
    }
    

    // Get photos
    const photos = listing.Media || [];
    const photoUrls = photos
      .filter(media => media.MediaCategory === 'Photo')
      .sort((a, b) => (a.Order || 0) - (b.Order || 0))
      .map(media => media.MediaURL);

    // Format agent name
    const agentFirstName = listing.ListAgentFirstName || '';
    const agentLastName = listing.ListAgentLastName || '';
    const agentName = `${agentFirstName} ${agentLastName}`.trim();

    // Create an open house entry (single entry per listing since it's flat structure)
    const startTime = new Date(listing.OpenHouseStartTime);
    const endTime = new Date(listing.OpenHouseEndTime);
    
    // Generate unique ID
    const id = `${listing.ListingKey}_${startTime.getTime()}`;

    openHouses.push({
      id,
      listingKey: listing.ListingKey,
      listingId: listing.ListingId || '',
      openHouseStartTime: startTime,
      openHouseEndTime: endTime,
      openHouseRemarks: listing.OpenHouseRemarks || '',
      address: listing.UnparsedAddress || '',
      price: listing.ListPrice || 0,
      beds: listing.BedroomsTotal || 0,
      // Ensure baths is a proper integer (not float or string) to prevent "00" display issue
      baths: listing.BathroomsTotalInteger != null ? parseInt(listing.BathroomsTotalInteger, 10) : null,
      // Manufactured homes use BuildingAreaTotal, others use LivingArea
      // The server already handles this mapping, so we just use LivingArea here
      sqft: listing.LivingArea || null,
      city: listing.City || '',
      state: listing.StateOrProvince || '',
      postalCode: listing.PostalCode || '',
      latitude: listing.Latitude || 0,
      longitude: listing.Longitude || 0,
      photo: photoUrls[0] || '',
      photos: photoUrls,
      agentFirstName,
      agentLastName,
      agentName,
      subdivisionName: listing.SubdivisionName || '',
      // ADD THESE HOST FIELDS HERE (before raw)
      hostMemberKey: listing.HostMemberKey || null,
      hostDisplayName: listing.HostDisplayName || null,
      hostFirstName: listing.HostFirstName || null,
      hostLastName: listing.HostLastName || null,
      raw: listing
    });
  });

  // Sort by open house start time (earliest first), then by end time (earliest first) as secondary sort
  return openHouses.sort((a, b) => {
    const startTimeDiff = a.openHouseStartTime - b.openHouseStartTime;
    if (startTimeDiff !== 0) {
      return startTimeDiff;
    }
    // If start times are equal, sort by end time (earliest first)
    return a.openHouseEndTime - b.openHouseEndTime;
  });
};

/**
 * Load open houses data from API (real-time only)
 */
export async function loadOpenHousesData() {
  if (isDataLoading) {
    return dataLoadPromise;
  }
  
  // Check cache first (with version check)
  const cacheKey = `openhouses_data_v${CACHE_VERSION}`;
  const cachedData = simpleCache.get(cacheKey);
  if (cachedData) {
    // Reconstruct Date objects from cached data (localStorage serializes Dates as strings)
    const reconstructedData = cachedData.map(oh => ({
      ...oh,
      openHouseStartTime: new Date(oh.openHouseStartTime),
      openHouseEndTime: new Date(oh.openHouseEndTime)
    }));
    openHousesData = reconstructedData;
    return reconstructedData;
  }
  
  // Clear old cache versions
  try {
    for (let i = 1; i < CACHE_VERSION; i++) {
      simpleCache.remove(`openhouses_data_v${i}`);
    }
    simpleCache.remove('openhouses_data'); // Clear old unversioned cache
  } catch (e) {
    // Ignore errors
  }
  
  isDataLoading = true;

  dataLoadPromise = (async () => {
    try {
      let baseUrl = 'http://localhost:4000';
      
      // Use network IP when accessing from mobile device
      if (window.location.hostname === '192.168.4.177') {
        baseUrl = 'http://192.168.4.177:4000';
      } else if (import.meta.env.MODE === 'production' || import.meta.env.MODE === 'staging') {
        baseUrl = 'https://rmkp-open-houses.onrender.com';
      }
      
      const url = `${baseUrl}/api/openhouses`;
      
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Agent-ID': agent.sparkAgentId,
        },
        credentials: import.meta.env.MODE === 'production' ? 'include' : 'same-origin'
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
      }
      
      const data = await res.json();
      const listings = data.value || [];
      
      // Transform the data
      const transformedData = transformOpenHouseData(listings);
      
      // Apply date filtering - each open house event is filtered individually
      const filteredData = getRelevantOpenHouses(transformedData);
      
      // Debug: Log filtering results
      if (import.meta.env.MODE === 'development') {
        console.log(`📅 Date filtering: ${transformedData.length} events → ${filteredData.length} events`);
        const now = new Date();
        const today = now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        console.log(`📅 Today is: ${today}`);
        filteredData.forEach(oh => {
          const date = new Date(oh.openHouseStartTime);
          const dateStr = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
          console.log(`  ✓ ${oh.address.substring(0, 30)}... - ${dateStr}`);
        });
      }
      
      // Cache the data for 5 minutes (with version)
      simpleCache.set(cacheKey, filteredData);
      
      // Preload first few images for better performance
      preloadImages(filteredData);
      
      openHousesData = filteredData;
      
      return openHousesData;
    } catch (error) {
      console.error('Failed to load open houses data:', error);
      throw error;
    } finally {
      isDataLoading = false;
    }
  })();

  return dataLoadPromise;
}

/**
 * Get open houses data (real-time only)
 * Will load the data if not already loaded
 */
export const getOpenHouses = async () => {
  if (!openHousesData) {
    await loadOpenHousesData();
  }
  return openHousesData;
};

/**
 * React hook to use open houses data
 */
export const useOpenHouses = () => {
  const [openHouses, setOpenHouses] = useState(openHousesData || []);
  const [loading, setLoading] = useState(!openHousesData);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Always check cache validity - if localStorage cache is expired/missing,
    // reload even if openHousesData is set (might be stale from before code changes)
    const cacheKey = `openhouses_data_v${CACHE_VERSION}`;
    const cachedData = simpleCache.get(cacheKey);
    if (!cachedData && openHousesData) {
      // Cache expired but in-memory data exists - clear it and reload
      openHousesData = null;
    }
    
    if (!openHousesData) {
      setLoading(true);
      loadOpenHousesData()
        .then(() => {
          setOpenHouses(openHousesData);
          setLoading(false);
        })
        .catch(err => {
          setError(err);
          setLoading(false);
        });
    } else {
      setOpenHouses(openHousesData);
    }
  }, []);

  return { 
    openHouses,
    loading, 
    error 
  };
};

/**
 * Preload images for better performance
 * Only preloads first 8 images to avoid overwhelming the browser
 */
function preloadImages(openHouses) {
  if (!openHouses || openHouses.length === 0) return;
  
  // Get first 8 primary photos
  const imageUrls = openHouses
    .slice(0, 8)
    .map(house => house.photo)
    .filter(url => url && url.trim() !== '');
  
  // Preload optimized images in background
  imageUrls.forEach(url => {
    const img = new Image();
    img.src = getGalleryImageUrl(url); // Use optimized gallery size
    // Don't wait for load - just start the download
  });
}

/**
 * Clear cache and force refresh (for manual refresh)
 */
export function clearCache() {
  // Clear current version and old versions
  simpleCache.remove(`openhouses_data_v${CACHE_VERSION}`);
  for (let i = 1; i < CACHE_VERSION; i++) {
    simpleCache.remove(`openhouses_data_v${i}`);
  }
  simpleCache.remove('openhouses_data'); // Clear old unversioned cache
  openHousesData = null;
}